import React, { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  updateAboutUsMission,
  getAboutUsMission,
  createAboutUsMission,
} from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";
import { Formik } from "formik";
import * as Yup from "yup";

// Validation Schema
const validationSchema = Yup.object({
  paragraph: Yup.string()
    .required("Paragraph is required"),
});

export default function MissionEdit() {
  const { id, partnerId } = useParams();
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();
  const [paragraph, setParagraph] = useState({
    paragraph: "",
    index: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const getInitialValues = () => ({
    paragraph: paragraph.paragraph || "",
  });

  const fetchMissionData = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await getAboutUsMission(id);
      const paraData = response.data.data[0];
      setParagraph({
        ...paraData,
        index: response.data.data.indexOf(paraData),
      });
    } catch (error) {
      console.log(error);
      showToast({
        type: 'error',
        message: error?.response?.data?.message || "Failed to fetch mission data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMissionData();
  }, [id]);

  const createTitle = async (formData) => {
    setIsUpdating(true);
    try {
      const response = await createAboutUsMission(formData);
      if (response.status === 200) {
        showToast({
          type: 'success',
          message: response?.data?.message || "Mission section has been created successfully!",
        });
        const missionData = response?.data?.data;
        setParagraph(missionData);
        if (missionData && missionData.id) {
          navigate(`/${partnerId}/aboutus/mission/edit/${missionData.id}`);
        }
      } else {
        showToast({
          type: 'error',
          message: response?.data?.message || "Failed to create mission section. Please try again.",
        });
      }
      setIsUpdating(false);
    } catch (error) {
      showToast({
        type: 'error',
        message: error?.response?.data?.message || "Failed to create mission section. Please try again.",
      });
      console.error(error);
      setIsUpdating(false);
    }
  };

  const updateTitle = async (id, formData) => {
    setIsUpdating(true);
    try {
      const response = await updateAboutUsMission(id, formData);
      showToast({
        type: 'success',
        message: response?.data?.message || "Mission section has been updated successfully!",
      });
      setParagraph(response.data.data);
    } catch (error) {
      showToast({
        type: 'error',
        message: error?.response?.data?.message || "Failed to update mission section. Please try again.",
      });
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSubmit = async (values) => {
    const formData = {
      paragraph: values.paragraph,
    };
    
    if (!Number(id)) {
      formData.partner_id = partnerId;
      await createTitle(formData);
    } else {
      await updateTitle(id, formData);
    }
  };

  return (
    <>
      {isLoading ? <CmsCircularProgress/> :
        <FormWrapper>
          <Title>{Number(id) ? "Edit" : "Add"} Mission section</Title>
          <SliderDiv>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({
                values,
                errors,
                touched,
                handleSubmit,
                getFieldProps,
                isSubmitting,
              }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <FormGroup 
                    label="Enter a New Mission Paragraph" 
                    requiredField
                    error={touched.paragraph && errors.paragraph ? errors.paragraph : ''}
                  >
                    <Form.Control
                      as="textarea"
                      rows={4}
                      {...getFieldProps('paragraph')}
                      placeholder="Enter mission paragraph here..."
                      isInvalid={touched.paragraph && !!errors.paragraph}
                    />
                  </FormGroup>
                  
                  <ButtonWrapper className="mt-3">
                    <Link
                      to={`/${partnerId}/aboutus/mission/view/${Number(id) ? id : "create"}`}
                      style={{
                        width: "50%",
                      }}
                    >
                      <Button variant="secondary">Go Back</Button>
                    </Link>
                    <Button
                      type="submit"
                      style={{
                        width: "50%",
                      }}
                      disabled={isUpdating || isSubmitting}
                    >
                      {isUpdating ? "Saving..." : "Save Changes"}
                    </Button>
                  </ButtonWrapper>
                </Form>
              )}
            </Formik>
          </SliderDiv>
        </FormWrapper>
      }
    </>
  );
}

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
  @media (min-width:576px) {
    padding: 1rem;
  }
  @media (min-width:768px) {
    padding: 1.5rem;
  }
  padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  gap: 2rem;
`;
